<template>
    <div data-test-view="goals-tree">
        <l-card-content>
            <!-- Header -->
            <l-headline :title="headline.title" :title-popover="headline.popover" :tabs="tabs" />
            <!--/Header -->

            <l-goal-filters />

            <l-space :size="2" />

            <!-- Tree -->
            <div v-if="goals.length">
                <l-goal-tree
                    :data="data"
                    :config="{ zoom, draggable, center }"
                    :show-menu="showMenu"
                    @update="updateParentGoal"
                />
            </div>

            <div v-else class="text-center">
                <h3 class="color-grey">{{ $t(`goals.list.treeEmptyH1`) }}</h3>
                <p>
                    <a
                        href="https://leapsome.zendesk.com/hc/en-us/categories/115000366973-Goals"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {{ $t(`goals.list.treeEmptyH3`) }}
                    </a>
                    <span>{{ $t(`goals.list.treeEmptyH4`) }}</span>
                </p>

                <br />

                <div class="text-center">
                    <a
                        :href="screenshotGoalsTreeCircle"
                        aria-label="Goal Tree placeholder"
                        target="_blank"
                    >
                        <img :src="screenshotGoalsTreeCircle" width="450" alt="Goal Tree" />
                    </a>
                </div>
            </div>
            <!--/Tree -->
        </l-card-content>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LCardContent from "@/components/LCardContent";
import LHeadline from "@/components/LHeadline";
import LGoalTree from "@/components/LGoalTree";
import LGoalFilters from "@/components/LGoalFilters";

import screenshotGoalsTreeCircle from "@/img/screenshots/GoalsTreeCircle.jpg";
import iconRocket from "@/img/icons/rocket.png";
import LSpace from "@/components/LSpace.vue";

export default {
    components: {
        LCardContent,
        LHeadline,
        LGoalTree,
        LGoalFilters,
        LSpace,
    },
    props: {
        segment: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            zoom: 1.0,
            step: 0.05,
            center: null,
            showMenu: true,
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            user: "users/current/user",
            goals: "goals/goals/list",
        }),
        headline() {
            switch (this.segment) {
                case "user":
                    return {
                        title: this.$t(`goals.list.titleMyGoals`),
                        popover: this.$t(`goals.list.subtitleMyGoals`),
                    };
                case "team":
                    return {
                        title: this.$t(`goals.list.titleMyTeam`),
                        popover: this.$t(`goals.list.subtitleMyTeam`),
                    };
                case "company":
                    return {
                        title: this.$t(`goals.list.titleCompanyTeam`),
                    };
                case "all":
                default:
                    return {
                        title: this.$t(`goals.list.titleGoalTree`),
                    };
            }
        },
        data() {
            return {
                name: this.user.teamSettings.vision
                    ? this.user.teamSettings.vision
                    : this.user.teamSettings.name,
                title: this.user.teamSettings.vision ? this.user.teamSettings.name : "",
                children: this.goals,
                icon: iconRocket,
                _id: "root",
            };
        },
        draggable() {
            return this.user.rights.G_ACCESS_ALL;
        },
        tabs() {
            return [
                {
                    show: this.showMenu,
                    label: this.$t(`goals.list.hideMenu`),
                    icon: "fal fa-arrow-alt-to-left",
                    iconBeforeLabel: true,
                    testId: "goals-tree-hide-menu",
                    handler: this.toggleMenu,
                },
                {
                    show: !this.showMenu,
                    label: this.$t(`goals.list.showMenu`),
                    testId: "goals-tree-hide-menu",
                    handler: this.toggleMenu,
                },
                {
                    label: this.$t(`goals.list.treeAssignExplanation`),
                    show: this.rights.G_ACCESS_ALL,
                    tabClass: "descriptive",
                    testId: "goals-tree-assign-explanation",
                },
                {
                    label: this.$t(`goals.list.treeExplanation`),
                    show: true,
                    tabClass: "descriptive",
                    testId: "goals-tree-explanation",
                },
                {
                    label: "",
                    icon: "fal fa-search-plus",
                    show: true,
                    handler: () => this.goalTreeZoomIn(),
                    testId: "goals-tree-zoom-in",
                },
                {
                    label: "",
                    icon: "fal fa-search-minus",
                    show: true,
                    handler: () => this.goalTreeZoomOut(),
                    testId: "goals-tree-zoom-out",
                },
                {
                    label: "",
                    icon: "fal fa-bullseye",
                    show: true,
                    handler: () => this.goalTreeZoomReset(),
                    testId: "goals-tree-zoom-reset",
                },
                {
                    tabClass: "space",
                    show: true,
                },
                {
                    label: this.$t(`goals.list.btnCreate`),
                    handler: () => this.createNewGoal(),
                    show: true,
                    isButton: true,
                    tabClass: "btn-primary",
                },
            ];
        },
    },
    created() {
        this.$emit("section", "tree");
        this.screenshotGoalsTreeCircle = screenshotGoalsTreeCircle;
    },
    methods: {
        createNewGoal() {
            this.$emit("create");
        },
        toggleMenu() {
            this.showMenu = !this.showMenu;
            this.$emit("toggleMenu");
        },
        goalTreeZoomIn() {
            this.zoom += this.step;
        },
        goalTreeZoomOut() {
            if (this.zoom <= this.step) {
                return;
            }

            this.zoom -= this.step;
        },
        goalTreeZoomReset() {
            this.zoom = 1.25;
            this.center = { x: 0, y: 0 };

            this.$nextTick(() => {
                this.center = null;
            });
        },

        async updateParentGoal({ id, parent }) {
            await this.$store.dispatch({
                type: "goals/goals/update",
                payload: {
                    goal: {
                        scope: "parent",
                        _id: id,
                        parent: parent === "root" ? "" : parent,
                    },
                },
            });

            await Promise.all(
                this.$route.meta.resolvers.map(resolver =>
                    this.$store.dispatch(resolver(this.$route)),
                ),
            );
        },
    },
};
</script>

<style scoped></style>
